import React from 'react';
import SeoToolPage from './SeoToolPage';

export default function TikTokShadowbanChecker() {
  return (
    <SeoToolPage
      canonicalPath="/tiktok-shadowban-checker"
      title="TikTok Shadowban Checker (Free) | Merra AI"
      description="Free TikTok shadowban checker. Enter your handle to see if you're shadowbanned—and what to do next if views feel stuck."
      keywords="tiktok shadowban checker, am i shadowbanned tiktok, how to know if shadowbanned tiktok"
      h1="TikTok shadowban checker"
      subheading="Enter your TikTok handle. We'll tell you whether you're shadowbanned—and what actually fixes low views."
      platformLabel="TikTok"
      handlePlaceholder="@yourtiktok"
      inputLabel="TikTok handle"
      ctaLabel="Check shadowban"
      analyzingLabel="Checking…"
      resultTitle="You're not shadowbanned."
      resultParagraphs={[
        'If your videos get more than 0 views, you are not shadowbanned. TikTok is still distributing your content—your content just isn\'t winning the retention test yet.',
        'Shadowbans are rare. Low views are normal when hooks are weak, pacing is slow, or you haven\'t posted enough tests to find what works.',
        'The Merra way: post more trial videos (about 5 TikToks a day), study retention, and iterate hooks until something breaks out. Film raw clips, then let Merra AI turn them into ready-to-post videos so you can test volume without living in an editor.',
      ]}
      resultBullets={[
        'Post better content: stronger first 1–2 seconds',
        'Post more: ~5 trial TikToks per day while learning',
        'Focus on retention: watch-time and rewatches beat vanity views',
        'Use Merra AI to turn clips into scripts, voiceover, captions, and edits fast',
      ]}
      sections={[
        {
          title: 'What a real shadowban looks like',
          body: 'A true distribution restriction usually means near-zero reach across new posts for an extended period—not "stuck at a few hundred views." If people can find your profile and your posts have any views, your problem is content quality and testing speed, not a ban.',
        },
        {
          title: 'The Merra way out of low views',
          body: 'Stop polishing one video for hours. Shoot once, generate multiple variants with Merra AI, post ~5 times a day, and keep the hooks that hold attention. Consistency + retention beats waiting for the perfect edit.',
        },
      ]}
      faqs={[
        {
          q: 'How do I know if I\'m shadowbanned on TikTok?',
          a: 'If your videos get more than 0 views, you\'re not shadowbanned. Low views usually mean weak hooks, slow pacing, or too few experiments—not a ban.',
        },
        {
          q: 'Why are my TikToks stuck at low views?',
          a: 'The algorithm tests every video. If retention is weak in the first seconds, distribution stops. Fix the hook and post more trials.',
        },
        {
          q: 'How often should I post on TikTok?',
          a: 'During a learning phase, aim for about 5 TikToks a day so you can find winning formats faster.',
        },
      ]}
      relatedLinks={[
        { to: '/instagram-shadowban-checker', label: 'Instagram shadowban checker' },
        { to: '/when-to-post-tiktok', label: 'When to post on TikTok' },
        { to: '/ai-video-maker', label: 'AI video maker' },
      ]}
      relatedBlogLinks={[
        { to: '/blog/how-to-know-if-shadowbanned-tiktok', label: 'How to know if you\'re shadowbanned on TikTok' },
        { to: '/blog/tiktok-stuck-at-200-views', label: 'TikTok stuck at 200 views' },
        { to: '/blog/how-to-increase-video-retention', label: 'How to increase video retention' },
      ]}
    />
  );
}
